// The Overwolf change-plan page (/ow-plan).
//
// Opened from the Songhunt menu inside the Overwolf app, e.g.
//   /songhunt/ow-plan?d=<SH1 blob>&utm_source=overwolf&utm_campaign=ow_change_plan
//
// The `d` blob carries the signed-in user's e-mail, password and current plan
// (see api/account-link.ts, and the security note in it), so the page logs them
// in silently and never asks a user who is already signed in to sign in again.
//
// The password in it can be EMPTY - the app still sends the link when a restored
// session has no stored password. That is not a broken link: it names the account
// without the secret, so the page shows the plans and asks for the password, the
// same step the signed-out entries use.
//
// It is also reachable with NO blob at all - from an e-mail, or as a bare link
// anyone can be sent:
//
//   /songhunt/ow-plan?email=<address>&utm_source=email&utm_medium=email
//                    &utm_campaign=ow_plan_expiring
//
// A mail cannot mint an SH1 payload - that needs the plaintext password, which
// only the Overwolf client ever holds - so that entry is signed out: the page
// shows the plans, and asks for a password only once the user has picked one,
// right before the checkout that needs a session. It cannot say which plan they
// are on until then, so it does not pretend to.
//
// Without `email` the same page asks for the address at sign-in instead, so a
// plain /ow-plan works for everyone and needs no per-recipient link.
//
// The campaign is what separates the entries in analytics, so it is recorded in
// the diagnostic log below - the app sends ow_change_plan, a mail sends its own.
//
// Shares its constraints with OwSurvey and OwCancel:
//
// - It renders OUTSIDE the App shell (a top-level route in app/router.tsx), so
//   there is no NavBar, no store bootstrap and no session to inherit. Changing a
//   plan never leaves this page: there is no endpoint behind it yet, so the
//   change is applied to the card in place and the team is notified out of band.
// - Because AppLoader is not mounted, this route removes the static
//   #app-preloader itself (see the react-basename-bare-path-preloader-hang note).
// - The affiliate-link popup must never appear at someone managing their
//   subscription. The link always carries utm_source=overwolf, and setting the
//   UTM tags below latches isOverwolfSource(), which hard-disables the timer for
//   the rest of the tab session even though login() would otherwise start it.
//
// LOGGING RULE (from the account-link spec): log THAT a link was opened and what
// happened, never the URL, the blob, or any decoded field. These logs get pasted
// into support tickets.

import { useCallback, useEffect, useMemo, useState } from 'react';
import SonghuntLogo from '../../shared/components/SonghuntLogo';
import { assetUrl } from '../../shared/asset-url';
import { logger } from '../../shared/logging/logger';
import { checkPromoCode, createCheckoutSession, login as apiLogin } from '../../api/http-req';
import { decodeAccountPayload, type AccountPlan } from '../../api/account-link';
import AccountRequestEmailStep from '../../shared/support/AccountRequestEmail';
import { buildPlanChangeEmail } from '../../shared/support/account-request-email';
import { discountedPrice } from '../../shared/modals/plan-price';
import { useUserStore } from '../../stores/user-store';
import type { Coupon } from '../../interfaces/coupon';
import type { User } from '../../interfaces/user';
import './OwPlan.scss';

type Status =
  | 'checking'     // decoding + signing in
  | 'ready'        // we know who they are and what they are on
  | 'choose'       // arrived from an e-mail: plans shown, nobody signed in yet
  | 'checkout'     // handing off to Stripe for a paid plan
  | 'unavailable'; // link missing or malformed, or the sign-in was rejected

interface PlanRow {
  key: AccountPlan;
  name: string;
  /** One line of who the plan is for, shown in the card's banner. */
  sub: string;
  /** Confirmed 2026-08-22. Must match the signup landing's Overwolf prices. The
   *  signup landing's first-month promo still belongs to signup only; the one
   *  discount this page carries is the Streamer coupon below, which is a standing
   *  offer on that plan rather than a signup incentive. */
  price: string;
  features: string[];
  /** What is lost by leaving this plan - shown in the downgrade confirmation. */
  loses: string[];
}

// Ordered cheapest to richest: the row above yours is a downgrade, the row below
// is an upgrade, and the page derives the direction from this order alone.
const PLAN_ROWS: PlanRow[] = [
  {
    key: 'none',
    name: 'No plan',
    sub: 'Just trying it out',
    price: '$0.00',
    features: ['30 minutes of Adaptive Music per session', 'Unlimited Prompt Song Searches'],
    loses: [],
  },
  {
    key: 'personal',
    name: 'Personal Use',
    sub: 'For playing on your own',
    price: '$5.00',
    features: [
      'Unlimited Adaptive Music',
      'Unlimited Prompt and Filter Song Searches',
      'Explorer Songhunt.ai access',
    ],
    loses: ['Unlimited Adaptive Music - back to 30 minutes a session', 'Explorer Songhunt.ai access'],
  },
  {
    key: 'streamer',
    name: 'Streamer',
    sub: 'For Streamers and Creators',
    price: '$15.00',
    features: [
      'Everything in Personal Use',
      "'Stream Mode' - 100% licensed music you can stream",
      'Unlimited Rematches',
      'Hunter Songhunt.ai access',
    ],
    loses: [
      "'Stream Mode' - 100% licensed music you can stream",
      'Unlimited Rematches',
      'Hunter Songhunt.ai access',
    ],
  },
];

const rank = (plan: AccountPlan) => PLAN_ROWS.findIndex(row => row.key === plan);

// The Overwolf plans are their own Stripe products (confirmed 2026-08-24); the
// web modal's product 5 is a different price and must not be used here. 'none'
// is not for sale - you arrive there by letting a plan lapse.
const STRIPE_PRODUCT: Record<AccountPlan, number | null> = {
  none: null,
  personal: 6,
  streamer: 7,
};

// The standing coupon on Streamer. Applied automatically - this page has no
// coupon field, so nobody types it and nobody has to be told it exists.
//
// Spelling matters: 'Streamer', exactly as it is in Stripe (confirmed
// 2026-08-26). Stripe restricts it to product 7, so it is Streamer-only at the
// source, not just by the `row.key` check in startCheckout below - the code
// cannot discount Personal Use even if it were somehow sent with it.
//
// It is only ever OFFERED once the backend has confirmed it: the page asks
// checkPromoCode() what the code is worth and shows a discount only if the answer
// carries one. A code that Stripe does not know (renamed, expired, withdrawn)
// leaves the card at its plain $15.00 and sends no code to checkout, so the page
// cannot promise money off that the checkout will not take.
const STREAMER_COUPON = 'Streamer';

/** The card's own price without the currency symbol - what discountedPrice() takes. */
const bareAmount = (price: string) => price.replace(/[^0-9.]/g, '');

/**
 * Which card a coupon is allowed to discount, or null for none of them.
 *
 * check-promo-code takes no product id, but its answer names the price the coupon
 * is priced against - verified against the live endpoint on 2026-08-26:
 *
 *   Streamer -> {"price":"15.00","reduction":33.4}    the $15.00 card
 *   HOLIDAY  -> {"price":"5.00","reduction":100.0}    the $5.00 card
 *   VIP24    -> {"price":"0.00","reduction":100}      no card sells this
 *   unknown  -> {"price":null}
 *
 * Matching that price to a card is what keeps a typed code honest: a $5-off code
 * cannot paint a discount on the $15.00 plan that Stripe would then refuse. A
 * coupon that matches nothing purchasable discounts nothing.
 */
function couponTarget(coupon: Coupon | null): AccountPlan | null {
  if (!coupon || coupon.price == null) return null;

  const priced = Number(coupon.price);
  if (!Number.isFinite(priced)) return null;

  const row = PLAN_ROWS.find(
    candidate =>
      STRIPE_PRODUCT[candidate.key] != null &&
      Number(bareAmount(candidate.price)) === priced,
  );
  return row ? row.key : null;
}


/** Keeps the affiliate timer off for this tab (see the header note). */
function latchOverwolfUtm(params: URLSearchParams): void {
  const utm = new URLSearchParams();
  for (const [key, value] of params) {
    if (key.startsWith('utm_')) utm.set(key, value);
  }
  if ([...utm.keys()].length > 0) useUserStore.getState().setUtmTags(utm.toString());
}

export default function OwPlan() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);

  const [status, setStatus] = useState<Status>('checking');
  // null while nobody is signed in: the e-mail entry genuinely does not know.
  const [currentPlan, setCurrentPlan] = useState<AccountPlan | null>(null);
  const [email, setEmail] = useState('');
  const [user, setUser] = useState<User | null>(null);
  // One dialog, two steps. A downgrade opens at 'confirm' and moves to 'email'
  // when the user goes through with it; a free upgrade has nothing to warn about,
  // so it opens straight at 'email'. Both end in the same place, which is why the
  // e-mail step lives in the dialog rather than on the page behind it.
  const [dialogPlan, setDialogPlan] = useState<PlanRow | null>(null);
  const [dialogStep, setDialogStep] = useState<'confirm' | 'email'>('confirm');
  /** True when the checkout redirect has not happened in a reasonable time. */
  const [checkoutStalled, setCheckoutStalled] = useState(false);
  /** The plan picked from the signed-out page, waiting on a password. */
  const [pendingChoice, setPendingChoice] = useState<PlanRow | null>(null);
  /** True when the link named the account, so the sign-in needs only a password. */
  const [addressedByLink, setAddressedByLink] = useState(false);
  const [password, setPassword] = useState('');
  const [signInError, setSignInError] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState(false);
  /** What is typed in the coupon field. Prefilled with the Streamer coupon below,
   *  so the discount on the card is never anonymous - the code that produced it is
   *  visible, and can be replaced with another. */
  const [couponInput, setCouponInput] = useState('');
  /** The coupon actually in play: the code and what the backend says it is worth.
   *  One at a time - whatever is in the field, applied. */
  const [activeCoupon, setActiveCoupon] = useState<{ code: string; coupon: Coupon } | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponBusy, setCouponBusy] = useState(false);

  useEffect(() => {
    document.getElementById('app-preloader')?.remove();
    document.title = 'Songhunt - Your plan';
  }, []);

  // Price the Streamer coupon before the cards can offer it. Independent of the
  // sign-in above: the price on a card is the same whoever is looking, and the
  // signed-out entry sells Streamer too.
  useEffect(() => {
    let cancelled = false;

    checkPromoCode(STREAMER_COUPON).then(
      (coupon: Coupon | null) => {
        if (cancelled) return;
        // `!= null` (not `!==`): an unknown code comes back with `price: null`,
        // and a failed shape comes back undefined. Neither is a discount. This is
        // the same trap the signup card fell into - see LoginRegister's note.
        if (coupon?.price != null && couponTarget(coupon)) {
          setActiveCoupon({ code: STREAMER_COUPON, coupon });
          // Fills the field too: the user sees WHICH code is taking money off,
          // and the field stays the one place a coupon is changed.
          setCouponInput(STREAMER_COUPON);
          logger.info('app', 'ow-plan streamer coupon applied');
        } else {
          logger.info('app', 'ow-plan streamer coupon not available', { reason: 'no discount' });
        }
      },
      () => {
        // A coupon lookup is not worth a page. Full price is always honest.
        if (cancelled) return;
        logger.warn('app', 'ow-plan streamer coupon not available', { reason: 'lookup failed' });
      },
    );

    return () => {
      cancelled = true;
    };
  }, []);

  // Decode, validate, sign in. Runs once: the payload is a property of the URL.
  useEffect(() => {
    latchOverwolfUtm(params);

    const payload = decodeAccountPayload(params.get('d') ?? '');
    if (!payload) {
      // A blob that is PRESENT but unreadable is a dead end - something mangled
      // it, and guessing would be worse than saying so.
      if (params.get('d')) {
        logger.warn('app', 'ow-plan link rejected', { reason: 'malformed' });
        setStatus('unavailable');
        return;
      }

      // No blob: the signed-out entry. `email` is optional - when the link
      // carries it the sign-in asks only for a password, and when it does not the
      // page asks for both. Either way it is unverified input, used to prefill
      // and to label, never to authorise.
      const fromEmail = params.get('email')?.trim();
      logger.info('app', 'ow-plan opened signed out', {
        campaign: params.get('utm_campaign'),
        addressed: !!fromEmail,
      });
      if (fromEmail && fromEmail.includes('@')) {
        setEmail(fromEmail);
        setAddressedByLink(true);
      }
      setStatus('choose');
      return;
    }
    setEmail(payload.email);

    // The app sends a link with an empty password when the session was restored
    // without one. It knows WHO, not the secret - so ask for the secret rather
    // than dropping the user on a broken-link page, which is what the app-side
    // comment promises happens here.
    if (!payload.password) {
      logger.info('app', 'ow-plan opened without a password', {
        campaign: params.get('utm_campaign'),
      });
      setAddressedByLink(true);
      setStatus('choose');
      return;
    }

    // Only claimed once we are about to prove it. The plan in the payload is
    // unverified like everything else in a URL, and every other signed-out path
    // on this page refuses to name a plan it has not authenticated.
    setCurrentPlan(payload.plan);

    let cancelled = false;
    // Two-argument then(), NOT .then().catch(): only a REJECTED sign-in may put
    // the page into the failure state. With a trailing .catch(), anything that
    // threw inside the success handler - a store side effect, an analytics
    // script - would tell a user who just signed in fine that their link was
    // broken, and send them back to Overwolf to mint another one.
    apiLogin(payload.email, payload.password).then(
      (body: User) => {
        if (cancelled) return;

        try {
          useUserStore.getState().setUser(body);
        } catch (err) {
          // setUser injects analytics and touches several stores. None of that
          // is worth the page: they are signed in either way.
          logger.warn('app', 'ow-plan post-sign-in setup failed', { err: String(err) });
        }

        setUser(body);
        // Trust the live account over the link for the one plan we can verify:
        // a user who subscribed since the link was minted is on Streamer now.
        if (body?.isSubscribed) setCurrentPlan('streamer');
        logger.info('app', 'ow-plan opened', {
          plan: payload.plan,
          campaign: params.get('utm_campaign'),
        });
        setStatus('ready');
      },
      () => {
        if (cancelled) return;
        logger.warn('app', 'ow-plan link rejected', { reason: 'sign-in failed' });
        setStatus('unavailable');
      },
    );

    return () => {
      cancelled = true;
    };
  }, [params]);

  /** The plan the coupon in play discounts, if any. */
  const couponPlan = activeCoupon ? couponTarget(activeCoupon.coupon) : null;

  /**
   * Applies whatever is in the field. The field is the only source of truth: an
   * empty one means no coupon, including when the user has wiped the Streamer
   * code out. Clearing a discount you were given is a choice worth honouring, and
   * quietly putting it back would make the field a lie.
   */
  const applyCoupon = useCallback(() => {
    const code = couponInput.trim();
    setCouponError(null);

    if (!code) {
      setActiveCoupon(null);
      return;
    }
    if (couponBusy) return;

    setCouponBusy(true);
    checkPromoCode(code)
      .then(
        (coupon: Coupon | null) => {
          if (coupon?.price == null) {
            setActiveCoupon(null);
            setCouponError('No coupon found.');
            logger.info('app', 'ow-plan coupon rejected', { reason: 'unknown' });
            return;
          }
          // Known to the backend, but priced against something these cards do not
          // sell. Saying so beats painting a discount checkout would refuse.
          if (!couponTarget(coupon)) {
            setActiveCoupon(null);
            setCouponError('That coupon does not apply to these plans.');
            logger.info('app', 'ow-plan coupon rejected', { reason: 'other product' });
            return;
          }
          setActiveCoupon({ code, coupon });
          logger.info('app', 'ow-plan coupon applied', { plan: couponTarget(coupon) });
        },
        () => {
          // The lookup failed, which says nothing about the code. Leave what was
          // already applied alone rather than dropping a good discount.
          setCouponError('We could not check that coupon. Try again.');
          logger.warn('app', 'ow-plan coupon check failed');
        },
      )
      .finally(() => setCouponBusy(false));
  }, [couponBusy, couponInput]);

  // A successful checkout replaces the page, so this timer only ever fires when
  // the hand-off failed.
  useEffect(() => {
    if (status !== 'checkout') return;
    setCheckoutStalled(false);
    const stall = setTimeout(() => setCheckoutStalled(true), 8000);
    return () => clearTimeout(stall);
  }, [status]);

  // Moving UP is a purchase - both Personal Use ($5) and Streamer ($15) - so it
  // runs the real thing: the same Stripe checkout session normal signup uses. The
  // user is already signed in by the time they get here, which is exactly the
  // branch LoginRegister takes for a logged-in user.
  //
  // This is also what stops a second free month. The free month is a SIGNUP promo
  // that ends by dropping the account to No plan; coming back through this page
  // pays, because this path always goes to checkout and never grants a plan.
  const startCheckout = useCallback(
    (row: PlanRow) => {
      const productId = STRIPE_PRODUCT[row.key];
      if (productId == null) return; // 'none' is not a purchase

      // Only the plan the coupon is priced for carries it. Sent as the code rather
      // than an amount: Stripe applies it against the product being bought and
      // remains the authority on whether it is valid at all.
      const coupon = activeCoupon && couponPlan === row.key ? activeCoupon.code : null;

      logger.info('app', 'ow-plan checkout started', {
        from: currentPlan,
        to: row.key,
        coupon: !!coupon,
      });
      setStatus('checkout');
      // Redirects the page; nothing after this runs on the happy path.
      createCheckoutSession(coupon, productId);
    },
    [activeCoupon, couponPlan, currentPlan],
  );

  // Going DOWN has no endpoint and no payment to take, so the request leaves by
  // e-mail. The page shows what to send rather than pretending the plan moved.
  //
  // TODO: when a downgrade endpoint lands, call it here and drop the e-mail step.
  const applyChange = useCallback(
    (row: PlanRow) => {
      if (!currentPlan || rank(row.key) > rank(currentPlan)) {
        setDialogPlan(null);
        startCheckout(row);
        return;
      }

      logger.info('app', 'ow-plan change requested', { from: currentPlan, to: row.key });
      setDialogPlan(row);
      setDialogStep('email');
    },
    [currentPlan, startCheckout],
  );

  /**
   * Signs in from the e-mail entry, then does what the user already chose.
   *
   * The password is only ever asked for here, at the last moment before a
   * checkout that cannot work without a session. What happens next is decided by
   * the CHOICE, not by their current plan: the page still cannot tell 'No plan'
   * from 'Personal Use' (the account carries `isSubscribed`, not a tier), and it
   * does not need to - buying is buying.
   */
  const signInAndContinue = useCallback(() => {
    const row = pendingChoice;
    if (!row || signingIn) return;
    if (!email.includes('@')) {
      setSignInError('Enter the e-mail address on your Songhunt account.');
      return;
    }

    setSigningIn(true);
    setSignInError(null);

    apiLogin(email, password).then(
      (body: User) => {
        try {
          useUserStore.getState().setUser(body);
        } catch (err) {
          logger.warn('app', 'ow-plan post-sign-in setup failed', { err: String(err) });
        }
        setUser(body);
        setSigningIn(false);
        setPassword('');
        setPendingChoice(null);
        if (body?.isSubscribed) setCurrentPlan('streamer');

        // Already paying for the plan they just picked - charging again would be
        // the worst possible outcome of a helpful e-mail.
        if (row.key === 'streamer' && body?.isSubscribed) {
          setStatus('ready');
          return;
        }

        if (row.key === 'none') {
          setStatus('ready');
          setDialogPlan(row);
          setDialogStep('email');
          return;
        }

        startCheckout(row);
      },
      () => {
        setSigningIn(false);
        setSignInError('That password did not work. Try again.');
      },
    );
  }, [email, password, pendingChoice, signingIn, startCheckout]);

  const confirmDowngrade = useCallback(() => {
    if (dialogPlan) applyChange(dialogPlan);
  }, [applyChange, dialogPlan]);

  const closeDialog = useCallback(() => {
    setDialogPlan(null);
    setDialogStep('confirm');
  }, []);

  const currentRow = currentPlan ? PLAN_ROWS.find(row => row.key === currentPlan)! : null;
  /** The card the applied coupon names, for the line under the field. */
  const couponRow = couponPlan ? PLAN_ROWS.find(row => row.key === couponPlan)! : null;

  return (
    <div className="ow-plan">
      <main className="ow-plan__card">
        <div className="ow-plan__lockup">
          <SonghuntLogo className="ow-plan__sh-logo" />
          <span className="ow-plan__plus" aria-hidden="true">
            +
          </span>
          <img
            className="ow-plan__ow-logo"
            src={assetUrl('assets/logos/overwolf-logo.svg')}
            alt="Overwolf"
          />
        </div>

        {status === 'checking' && (
          <div className="ow-plan__result" role="status">
            <span className="ow-plan__spinner" aria-hidden="true" />
            <h1 className="ow-plan__title">Getting your plan...</h1>
          </div>
        )}

        {status === 'checkout' && (
          <div className="ow-plan__result" role="status">
            <span className="ow-plan__spinner" aria-hidden="true" />
            <h1 className="ow-plan__title">Taking you to secure checkout...</h1>
            <p className="ow-plan__sub">
              Your card details are handled by Stripe - we never see them.
            </p>
            {/* createCheckoutSession() swallows its own errors and redirects on
                success, so a page still sitting here means the hand-off failed.
                Say so rather than spinning forever. */}
            {checkoutStalled && (
              <>
                <p className="ow-plan__sub">
                  That is taking longer than it should. Check your connection and try again.
                </p>
                <button className="ow-plan__go" type="button" onClick={() => setStatus('ready')}>
                  Back to your plan
                </button>
              </>
            )}
          </div>
        )}

        {status === 'unavailable' && (
          <div className="ow-plan__result" role="status">
            <span className="ow-plan__badge ow-plan__badge--warn" aria-hidden="true">
              !
            </span>
            <h1 className="ow-plan__title">This link did not work.</h1>
            <p className="ow-plan__sub">
              We could not read your account from it. Open <b>Change plan</b> from the Songhunt menu
              in Overwolf again and we will pick up where you left off.
            </p>
            <a className="ow-plan__go" href="https://www.songhunt.ai/">
              Go to Songhunt.ai
            </a>
          </div>
        )}

        {(status === 'ready' || status === 'choose') && (
          <>
            <div className="ow-plan__head">
              <p className="ow-plan__eyebrow">Your plan</p>
              {/* Signed out, the page has no idea which plan they hold, so it
                  asks rather than announcing one. */}
              <h1 className="ow-plan__title">
                {currentRow ? `You are on ${currentRow.name}.` : 'Choose your plan.'}
              </h1>
              {email && (
                <span className="ow-plan__who">
                  <span className="ow-plan__avatar" aria-hidden="true">
                    {email[0]}
                  </span>
                  {status === 'choose' ? <>For <b>{email}</b></> : <>Signed in as <b>{email}</b></>}
                </span>
              )}
              {status === 'choose' && (
                <p className="ow-plan__sub">
                  Pick a plan and sign in to confirm it - we will ask for your Songhunt account
                  once, just before checkout.
                </p>
              )}
            </div>

            <ul className="ow-plan__cards">
              {PLAN_ROWS.map(row => {
                const isCurrent = row.key === currentPlan;
                const isUpgrade = !currentPlan || rank(row.key) > rank(currentPlan);
                // Signed out, every row is an "upgrade" from nothing, which would
                // paint all three primary and name no choice. The page makes the
                // same recommendation the signup landing does - Streamer - and
                // leaves the rest quiet.
                const isPrimary = status === 'choose' ? row.key === 'streamer' : isUpgrade;
                // The coupon is an offer to buy, so it only prices a card that
                // can be bought. On the plan they already hold there is nothing
                // to discount, and a struck-out price there would suggest their
                // next bill is changing.
                const withCoupon =
                  activeCoupon && row.key === couponPlan && !isCurrent
                    ? discountedPrice(bareAmount(row.price), activeCoupon.coupon)
                    : null;

                return (
                  <li
                    className={`ow-plan__card-item${isCurrent ? ' ow-plan__card-item--current' : ''}`}
                    key={row.key}
                  >
                    <div className="ow-plan__band">
                      {isCurrent && <span className="ow-plan__ribbon">YOUR PLAN</span>}
                      <h2 className="ow-plan__plan-name">{row.name}</h2>
                      <p className="ow-plan__plan-sub">{row.sub}</p>
                      <div className="ow-plan__price">
                        {withCoupon ? (
                          // Two stacked lines in the slot the single price uses:
                          // what it costs, struck through, over what they pay.
                          // No "/mo" under it - a 100% coupon reads "Free", and
                          // "Free /mo" is not a price. The signup card does the
                          // same with its discounted figure.
                          <span className="ow-plan__price-cut">
                            <span className="ow-plan__price-was">{row.price}</span>
                            <span className="ow-plan__price-now">{withCoupon}</span>
                          </span>
                        ) : (
                          <>
                            <span className="ow-plan__price-n">{row.price}</span>
                            <span className="ow-plan__price-p">/mo</span>
                          </>
                        )}
                      </div>
                    </div>

                    <ul className="ow-plan__features">
                      {row.features.map(feature => (
                        <li className="ow-plan__feature" key={feature}>
                          <span
                            className={`ow-plan__tick${row.key === 'none' ? ' ow-plan__tick--dim' : ''}`}
                            aria-hidden="true"
                          >
                            &#10003;
                          </span>
                          {feature}
                        </li>
                      ))}
                    </ul>

                    {/* The plan they hold keeps the slot so all three cards stay
                        the same height, but it is not a control. */}
                    {isCurrent ? (
                      <div className="ow-plan__cta ow-plan__cta--inert">This is your plan</div>
                    ) : (
                      <button
                        className={`ow-plan__cta ow-plan__cta--${isPrimary ? 'up' : 'down'}`}
                        type="button"
                        onClick={() => {
                          if (status === 'choose') {
                            // Nobody is signed in yet: collect the password first,
                            // because checkout needs a session to exist.
                            setSignInError(null);
                            setPassword('');
                            setPendingChoice(row);
                          } else if (isUpgrade) {
                            applyChange(row);
                          } else {
                            // Going down warns first; going up has nothing to warn about.
                            setDialogPlan(row);
                            setDialogStep('confirm');
                          }
                        }}
                      >
                        {status === 'choose'
                          ? `Choose ${row.name}`
                          : isUpgrade
                            ? `Upgrade to ${row.name}`
                            : `Switch to ${row.name}`}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>

            {/* Below the cards, because it is about a price the user has already
                seen. It comes prefilled with the Streamer code when that coupon
                is live, so the discount on the card above is never anonymous. */}
            <form
              className="ow-plan__coupon"
              onSubmit={event => {
                event.preventDefault();
                applyCoupon();
              }}
            >
              <label className="ow-plan__coupon-label" htmlFor="ow-plan-coupon">
                Have a coupon?
              </label>
              <div className="ow-plan__coupon-row">
                <input
                  className="ow-plan__coupon-input"
                  id="ow-plan-coupon"
                  type="text"
                  value={couponInput}
                  placeholder="Enter coupon code"
                  autoComplete="off"
                  autoCapitalize="none"
                  // Codes are case-sensitive at the backend ('hunter' is not
                  // 'Hunter'), so nothing here may helpfully re-case them.
                  spellCheck={false}
                  onChange={event => {
                    setCouponInput(event.target.value);
                    setCouponError(null);
                  }}
                />
                <button className="ow-plan__coupon-apply" type="submit" disabled={couponBusy}>
                  {couponBusy ? 'Checking...' : 'Apply'}
                </button>
              </div>

              {/* This page renders outside the App shell, so there is no toast
                  host to report into - the answer has to live next to the field.
                  Always rendered, so a coupon being applied does not shove the
                  cards up the page. */}
              <p
                className={`ow-plan__coupon-note${couponError ? ' ow-plan__coupon-note--warn' : ''}`}
                role={couponError ? 'alert' : 'status'}
              >
                {couponError ??
                  (activeCoupon && couponRow ? (
                    <>
                      <b>{activeCoupon.code}</b> applied to {couponRow.name}.
                    </>
                  ) : null)}
              </p>
            </form>

            <p className="ow-plan__foot">
              Changes apply to this account everywhere - the app and Songhunt.ai.
            </p>

            {/* The consent copy, word for word the same as the Overwolf signup
                landing's (LoginRegister.tsx) - the two pages sell the same plans,
                so they must not state different terms.

                Unlike signup, the Epidemic Sound clause is NOT conditional here.
                Signup knows which plan is being bought, so it only names the
                policy that plan agrees to; this page is a menu with Streamer
                always on it, and there is no single "chosen" plan to key off. */}
            <div className="ow-plan__legal">
              <p>
                {/* "By continuing", not the signup landing's "By registering": nobody
                    registers here. Everyone on this page already has an account and is
                    switching what they pay for. */}
                By continuing, you agree to receive emails from us, including updates,
                newsletters, and promotions. You can unsubscribe at any time by following the
                link in our emails. For details, see our{' '}
                <a href="https://www.mypart.ai/faq" target="_blank" rel="noopener noreferrer">FAQ</a>
              </p>
              <p>
                By continuing you also agree to{' '}
                <a href="https://www.mypart.ai/terms-conditions" target="_blank" rel="noopener noreferrer">
                  MyPart&apos;s Terms &amp; Conditions
                </a>{' '}
                and, for the licensed music in Stream Mode, the{' '}
                <a
                  href="https://www.epidemicsound.com/policy/v2/privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Epidemic Sound Privacy Policy
                </a>
                .
              </p>
            </div>
          </>
        )}
      </main>

      {/* Signed out, from the e-mail. One field, because the address came with
          the link - and a way out for the password nobody remembers, since there
          is no self-serve reset. */}
      {pendingChoice && (
        <div className="ow-plan__scrim" role="dialog" aria-modal="true" aria-labelledby="ow-plan-signin">
          <div className="ow-plan__dialog">
            <h2 className="ow-plan__dialog-title" id="ow-plan-signin">
              Sign in to choose {pendingChoice.name}.
            </h2>
            <p className="ow-plan__sub">
              {addressedByLink ? (
                <>
                  Confirming it as <b>{email}</b>.
                </>
              ) : (
                'Sign in to your Songhunt account to confirm it.'
              )}
            </p>

            <form
              className="ow-plan__signin"
              onSubmit={event => {
                event.preventDefault();
                signInAndContinue();
              }}
            >
              {/* Only asked for when the link did not carry it. */}
              {!addressedByLink && (
                <label className="ow-plan__field">
                  <span>Email</span>
                  <input
                    type="email"
                    value={email}
                    autoFocus
                    autoComplete="username"
                    onChange={event => setEmail(event.target.value)}
                  />
                </label>
              )}

              <label className="ow-plan__field">
                <span>Password</span>
                <input
                  type="password"
                  value={password}
                  autoFocus={addressedByLink}
                  autoComplete="current-password"
                  onChange={event => setPassword(event.target.value)}
                />
              </label>

              {signInError && (
                <p className="ow-plan__signin-error" role="alert">
                  {signInError}
                </p>
              )}

              <div className="ow-plan__dialog-actions">
                <button
                  className="ow-plan__confirm"
                  type="button"
                  onClick={() => {
                    setPendingChoice(null);
                    setPassword('');
                    setSignInError(null);
                  }}
                >
                  Back
                </button>
                <button className="ow-plan__keep" type="submit" disabled={!password || signingIn}>
                  {signingIn ? 'Signing in...' : 'Continue'}
                </button>
              </div>
            </form>

            <a
              className="ow-plan__dismiss"
              href={`mailto:songhunt@mypart.com?subject=${encodeURIComponent(`${email} forgot password`)}`}
            >
              Forgot your password?
            </a>
          </div>
        </div>
      )}

      {/* One dialog for both steps: a downgrade names what stops working before
          anything happens, and every change that needs an e-mail ends here with
          the message ready to send. The plans stay behind the scrim - this step
          is about the e-mail, not about picking again. */}
      {dialogPlan && (
        <div
          className="ow-plan__scrim"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ow-plan-confirm"
        >
          <div className="ow-plan__dialog">
            {dialogStep === 'confirm' ? (
              <>
                <span className="ow-plan__badge ow-plan__badge--warn" aria-hidden="true">
                  !
                </span>
                <h2 className="ow-plan__dialog-title" id="ow-plan-confirm">
                  Switch to {dialogPlan.name}?
                </h2>
                <p className="ow-plan__sub">Here is what you give up:</p>

                <ul className="ow-plan__loses">
                  {(currentRow?.loses ?? []).map(item => (
                    <li key={item}>
                      <span className="ow-plan__x" aria-hidden="true">
                        &times;
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>

                <div className="ow-plan__dialog-actions">
                  <button className="ow-plan__keep" type="button" onClick={closeDialog}>
                    Keep {currentRow?.name}
                  </button>
                  <button className="ow-plan__confirm" type="button" onClick={confirmDowngrade}>
                    Yes, switch
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="ow-plan__dialog-title" id="ow-plan-confirm">
                  One last step to move to {dialogPlan.name}.
                </h2>
                <p className="ow-plan__sub">
                  Plan changes are handled by a person, so we need it in writing. Send us an e-mail
                  with the subject below and we will move you and confirm
                  {email ? <> at <b>{email}</b></> : null}. Nothing changes until we receive it - you
                  keep {currentRow?.name ?? 'your current plan'} in the meantime.
                </p>

                <AccountRequestEmailStep
                  block="ow-plan"
                  request={buildPlanChangeEmail({
                    email,
                    fromPlan: currentRow?.name,
                    toPlan: dialogPlan.name,
                    // The SH1 link carries no id; this comes from the account we
                    // signed into, so it is only here when that succeeded.
                    userId: user?.id != null ? String(user.id) : undefined,
                  })}
                  action="Open my email app"
                />

                <button className="ow-plan__dismiss" type="button" onClick={closeDialog}>
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
